const BREAKPOINTS = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'] as const;

type Breakpoint = (typeof BREAKPOINTS)[number];

const X_VALUES = ['left', 'center', 'right'] as const;
const Y_VALUES = ['top', 'center', 'bottom'] as const;

const DEFAULT_X = 'center';
const DEFAULT_Y = 'center';

const SELECTION_PATTERN =
  /^(xs|sm|md|lg|xl|xxl) - (left|center|right|top|bottom)$/;

const parseSelections = (
  selections: string[] | undefined,
  allowed: readonly string[],
) => {
  const parsed: Partial<Record<Breakpoint, string>> = {};

  if (!Array.isArray(selections)) {
    return parsed;
  }

  selections.forEach((selection) => {
    const match = selection.match(SELECTION_PATTERN);
    if (!match) {
      return;
    }

    const [, breakpoint, value] = match;
    if (!allowed.includes(value)) {
      return;
    }

    parsed[breakpoint as Breakpoint] = value;
  });

  return parsed;
};

// Last tag for a breakpoint wins. A value set at a smaller breakpoint keeps
// applying until a later breakpoint sets that axis again.
export const getBackgroundObjectPosition = (
  positionX?: string[],
  positionY?: string[],
): string | Partial<Record<Breakpoint, string>> => {
  const xByBreakpoint = parseSelections(positionX, X_VALUES);
  const yByBreakpoint = parseSelections(positionY, Y_VALUES);
  const specified = new Set<Breakpoint>([
    ...(Object.keys(xByBreakpoint) as Breakpoint[]),
    ...(Object.keys(yByBreakpoint) as Breakpoint[]),
  ]);

  if (specified.size === 0) {
    return `${DEFAULT_X} ${DEFAULT_Y}`;
  }

  let x = DEFAULT_X;
  let y = DEFAULT_Y;
  const responsive: Partial<Record<Breakpoint, string>> = {};

  BREAKPOINTS.forEach((breakpoint) => {
    if (xByBreakpoint[breakpoint]) {
      x = xByBreakpoint[breakpoint];
    }
    if (yByBreakpoint[breakpoint]) {
      y = yByBreakpoint[breakpoint];
    }
    if (breakpoint === 'xs' || specified.has(breakpoint)) {
      responsive[breakpoint] = `${x} ${y}`;
    }
  });

  const keys = Object.keys(responsive);
  if (keys.length === 1) {
    return responsive.xs ?? `${DEFAULT_X} ${DEFAULT_Y}`;
  }

  return responsive;
};
